<template>
  <section class="n-view-register h-screen p-10 flex justify-center items-center">
    <NCard class="w-full max-w-[450px] shadow-md">
      <div class="text-center">
        <img class="mx-auto p-8" src="/images/logo-dark.png" alt="" />
      </div>
      <NForm ref="form" :model="model" :rules="rules" class="!grid-cols-1" @keydown.enter="onRegister()">
        <NSelect
          v-model:value="model.providerType"
          :all-option="false"
          :show-search="false"
          :options="ptOptions"
          v-bind="providerTypeAttrs"
          @change="onPtChange"
        />

        <NInputPreSuffix v-if="isMail" v-model:value="model.email" v-bind="emailAttrs">
          <template #suffix>
            <mail-outlined />
          </template>
        </NInputPreSuffix>

        <div v-if="isPhone" class="grid grid-cols-12 gap-1.5">
          <div class="col-start-1 col-end-6">
            <NSelect
              v-model:value="model.countryCode"
              :all-option="false"
              :show-search="false"
              :options="countryCodeOptions"
              v-bind="countryCodeTypeAttrs"
              @change="onCountryCodeChange"
            />
          </div>
          <div class="col-start-6 col-end-13">
            <NInputPreSuffix v-if="isPhone" v-model:value="model.phone" v-bind="phoneAttrs">
              <template #suffix>
                <mobile-outlined />
              </template>
            </NInputPreSuffix>
          </div>
        </div>

        <NInputSafePassword v-model:value="model.password" v-bind="passwordAttrs" />

        <NButton class="n-register-button" size="large" block type="primary" :loading="loading" @click="onRegister()">
          <span>{{ $tl('signUp') }}</span>
        </NButton>
      </NForm>
      <div class="pt-6 text-center text-base">
        <span class="mr-2">{{ $tl('alreadyHaveAccount') }}</span>
        <RouterLink to="/login">{{ $tl('login') }}</RouterLink>
      </div>
      <div>
        <NDivider class="text-base">{{ $tl('registerOtherWay') }}</NDivider>
      </div>

      <div class="grid grid-cols-3 gap-1.5">
        <NButton class="!w-full !h-[32px] !flex !justify-center !items-center" @click="onGoogle()">
          <template #icon>
            <NImage :src="'/images/login-ex-google.svg'" :preview="false" :width="18" />
          </template>
        </NButton>
        <NButton class="!w-full !h-[32px] !flex !justify-center !items-center" @click="onFacebook()">
          <template #icon>
            <NImage :src="'/images/login-ex-facebook.svg'" :preview="false" :width="18" />
          </template>
        </NButton>
        <NButton class="!w-full !h-[32px] !flex !justify-center !items-center" @click="onApple()">
          <template #icon>
            <NImage :src="'/images/login-ex-apple.svg'" :preview="false" :width="18" class="!h-[20px]" />
          </template>
        </NButton>
      </div>

      <ARow justify="center" :gutter="[16, 16]" class="n-view-register-agree">
        <ACol :span="24" class="text-center">
          <span class="n-agree-title">
            {{ $tl('registerAgreementTitle1') }}
            <a :href="agreePath" target="_blank">
              <span class="text-blue-600">{{ $tl('registerAgreementTitle2') }}</span>
            </a>
          </span>
          <NTooltip :visible="ttVisible" placement="top" :title="tl('registerPleaseAgree')" :color="tooltipColor">
            <ASwitch v-model:checked="agree" class="n-agree-switch" @change="onAgreeChange" />
          </NTooltip>
        </ACol>
      </ARow>

      <div class="mt-6 mb-3 text-center text-gray-600 grid items-center gap-3 grid-flow-col place-content-center">
        {{ COPYRIGHT }}
        <NChannelSwitch />
        <NLanguageSwitcher />
        <NHelper />
      </div>
    </NCard>
  </section>
</template>

<script setup>
import { useAppAction, useAppleQuery, useLanguage, useModelPost, useRouter, useValidate } from '@front/hooks';
import { register } from '@front/api/utility';
import { computed, reactive, ref } from 'vue';
import { AGREE_PDF_CONTENTS, COPYRIGHT, USERNAME_PROVIDER_TYPE, VALIDATION_RULE } from '@front/constant';
import { Col as ACol, Row as ARow, Switch as ASwitch } from 'ant-design-vue/es';
import { getDefaultAgent, getFrontURL, getMobiles } from '@/services';
import { isEmpty } from 'lodash-es';
import { generateMobiles } from "@front/utils";

const front = getFrontURL();
const defaultAgent = getDefaultAgent();
const agree = ref(false);
const tooltipColor = '#dc2626';
const agreeInitial = ref(true);
const { router, routerTo } = useRouter();
const { tl, t, curLang, PREFIX } = useLanguage();
const query = router.currentRoute.value.query;
const ptOptions = computed(() => [
  { label: tl('emailPassword'), value: 'MAIL_PASSWORD' },
  { label: tl('phonePassword'), value: 'PHONE_PASSWORD' },
]);
const sourceMobileOptions = getMobiles();
const countryCodeOptions = computed(() => { return generateMobiles(sourceMobileOptions);});

const isMail = computed(() => model.providerType === USERNAME_PROVIDER_TYPE.MAIL_PASSWORD);
const isPhone = computed(() => model.providerType === USERNAME_PROVIDER_TYPE.PHONE_PASSWORD);

const model = reactive({
  username: '',
  password: '',
  providerType: USERNAME_PROVIDER_TYPE.PHONE_PASSWORD,
  countryCode: '+55',
  email: undefined,
  phone: undefined,
});

const shareCode = computed(() => query?.shareCode ?? null);

const a = computed(() => query?.a ?? defaultAgent);
const agreePath = computed(() => {
  const agreeObj = AGREE_PDF_CONTENTS.find(o => o.lang === curLang.value);
  return agreeObj.content.value;
});
const ttVisible = computed(() => !agree.value && !agreeInitial.value);
const { setAction } = useAppAction();
const { setQuery } = useAppleQuery();

const { form, loading, validate, onPost } = useModelPost({
  post: customRegister,
});

const validation = computed(() => ({
  email: {
    required: model.providerType === USERNAME_PROVIDER_TYPE.MAIL_PASSWORD,
    isEmail: true,
    maxLength: VALIDATION_RULE.Customer.username.maxLength,
  },
  phone: {
    required: model.providerType === USERNAME_PROVIDER_TYPE.PHONE_PASSWORD,
    isMobile: true,
    maxLength: VALIDATION_RULE.Customer.mobile.maxLength,
  },
  countryCode: { required: model.providerType === USERNAME_PROVIDER_TYPE.PHONE_PASSWORD, maxlength: 5 },

  password: {
    required: true,
    maxLength: VALIDATION_RULE.Customer.password.maxLength,
  },
  providerType: { required: true },
}));
const { rules } = useValidate({ formRef: form, validation });

const passwordAttrs = computed(() => ({
  size: 'large',
  type: 'password',
  name: 'password',
  placeholder: tl('loginPassword'),
  disabled: loading.value,
}));

const providerTypeAttrs = computed(() => ({
  size: 'large',
  name: 'providerType',
  placeholder: tl('providerType'),
  disabled: loading.value,
}));

const countryCodeTypeAttrs = computed(() => ({
  size: 'large',
  name: 'countryCode',
  placeholder: tl('countryCode'),
  disabled: loading.value,
}));

const phoneAttrs = computed(() => ({
  size: 'large',
  name: 'phone',
  placeholder: tl('cellphone'),
  disabled: loading.value,
}));

const emailAttrs = computed(() => ({
  size: 'large',
  name: 'email',
  placeholder: tl('loginUsername'),
  disabled: loading.value,
}));

async function customRegister() {
  const params = {
    username: model.providerType === USERNAME_PROVIDER_TYPE.MAIL_PASSWORD ? model.email : model.countryCode + model.phone,
    type: model.providerType,
    password: model.password,
    shareCode: shareCode?.value ?? null,
    a: a?.value ?? null,
  };
  onFbEmailViewContent();

  // {"success":true,"data":{"success":true,"message":"成功"}}
  const result = await register(params);
  if (result.success) {
    // routerTo('/smsCode/' + params.username);
    const encode_name = encodeURIComponent(params.username);
    const encode_type = encodeURIComponent(params.type);
    const url = '/registerVerify/' + encode_name + '/' + encode_type;
    routerTo(url);
  }
}

async function onRegister() {
  setUpInitial();
  if (!agree.value) {
    return;
  }

  await onPost();
}

const onGoogle = () => {
  setUpInitial();
  if (!agree.value) return;
  setAction('REGISTER');
  const address = front + '/oauth2/authorization/google?action=QUICK_LOGIN&redirectUrl=' + front + '/%23/externalLoginVerify';
  const url1 = isEmpty(shareCode?.value) ? address : address.concat(`&shareCode=${shareCode.value}`);
  const url2 = isNaN(a?.value) ? url1 : url1.concat(`&a=${a.value}`);
  onFbExternalViewContent('google');
  window.open(url2, '_self');
};

const onFacebook = () => {
  setUpInitial();
  if (!agree.value) return;
  setAction('REGISTER');
  const address = front + '/oauth2/authorization/facebook?action=QUICK_LOGIN&redirectUrl=' + front + '/%23/externalLoginVerify';
  const url1 = isEmpty(shareCode?.value) ? address : address.concat(`&shareCode=${shareCode.value}`);
  const url2 = isNaN(a?.value) ? url1 : url1.concat(`&a=${a.value}`);
  onFbExternalViewContent('fb');
  window.open(url2, '_self');
};

const onApple = async () => {
  setUpInitial();
  if (!agree.value) return;

  setQuery({ shareCode, a });
  setAction('REGISTER');
  onFbExternalViewContent('apple');
  try {
    await AppleID.auth.signIn();
  } catch (e) {
    if (e.error !== 'popup_closed_by_user') throw e; // ignore apple popup page close -> "popup_closed_by_user"
  }
};

function onAgreeChange() {
  setUpInitial();
}

function onPtChange(old, nuw) {}

function onCountryCodeChange() {}

function setUpInitial() {
  agreeInitial.value = false;
}

function onFbEmailViewContent() {
  window.fbq?.('track', 'Lead', { content_name: 'registration', contents: { channel: 'email', agent: a?.value ?? undefined } });
}

function onFbExternalViewContent(chn) {
  window.fbq?.('track', 'ViewContent', {
    content_name: 'registration',
    contents: { channel: chn, agent: a?.value ?? undefined },
  });
}
</script>

<style lang="less">
.n-view-register {
  background-image: url('/images/shape01.png');
  background-position: center;

  .n-language-switcher {
    gap: 6px;
  }

  .n-gutter-box-text {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .n-view-register-agree {
    padding-top: 20px;
    align-items: center;
    justify-content: center;

    .n-agree-title {
      text-align: right;
      padding-top: 3px;
    }

    .n-agree-switch {
      width: 3.5em;
      text-align: left;
      margin-left: 10px;
    }
  }
}
</style>
